import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { CreateChancel, TimeInput, DateTypeSelect, Description, SelectAutocompletePagination } from 'components';
import { ErrorText, FindLoad, Images, FindErrorItem, useModal, reqTypeEnums, staffStatusEnums, renderStaffName } from 'utils';
import { scheduleModalsStyle } from './styles';
import { adminActions, appointmentActions, httpRequestsOnErrorsActions } from 'store';
import {
    apptOverlap,
    changeDateTime,
    checkBreakRequiresFields,
    checkDateSelector,
    defaultInitialInputs,
    getStartEndAppt,
    renderTimes,
    staffOverlap,
} from './constants';
import { ModalHeader } from './common';
import { CreateMultiple } from './createMultiple';
import { EditMultiple } from './editMultiple';
import { ConfMultiple } from './confMultiple';
import { TemplateApptActions } from './common/templateApptActions';
import { checkCreatePermission } from '../constants';

export const Unavailable = ({ handleOpenClose, type, date, modalDate, duplicate }) => {
    const { appointmentDates } = useSelector((state) => ({
        appointmentDates: state.appointment.appointmentDates,
    }));
    const editActionType = modalDate ? 'EDIT_APPOINTMENT' : 'CREATE_APPOINTMENT';
    const classes = scheduleModalsStyle();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const info = location?.state;
    const createModalDate = location?.state;
    const [inputs, setInputs] = useState(createModalDate ? { ...createModalDate } : {});
    const [error, setError] = useState({});
    const backError = FindErrorItem(editActionType);
    const loader = FindLoad(editActionType);
    const [createMultiple, setCreateMultiple] = useState(false);
    const [occurrence, setOccurrence] = useState(0);
    const [state, setState] = React.useState([]);
    const APPOINTMENT_FROM_TEMPLATE = 'APPOINTMENT_FROM_TEMPLATE';
    const { open } = useModal();
    const resetParams = modalDate ? modalDate : duplicate ? duplicate : null;
    const apptBetweenDates = {
        startDate: moment.utc(inputs?.betweenStartDate ? inputs?.betweenStartDate : appointmentDates?.startDate).format('YYYY-MM-DD'),
        endDate: moment.utc(inputs?.betweenEndDate ? inputs.betweenEndDate : appointmentDates?.endDate).format('YYYY-MM-DD'),
    };
    const reqType = checkCreatePermission(type);

    useEffect(() => {
        if (info?.staff && !modalDate && !duplicate && reqType === reqTypeEnums.ALL) {
            setInputs({
                ...inputs,
                staff: info?.staff,
            });
        }
    }, [info]);

    useEffect(() => {
        if (appointmentDates && (!inputs?.betweenStartDate || !inputs?.betweenEndDate)) {
            setInputs({
                ...inputs,
                betweenStartDate: appointmentDates?.startDate,
                betweenEndDate: appointmentDates?.endDate,
            });
        }
    }, [appointmentDates]);

    useEffect(() => {
        if (error) {
            setError('');
            dispatch(httpRequestsOnErrorsActions.removeError(editActionType));
            dispatch(httpRequestsOnErrorsActions.removeError('APPOINTMENT_OVERLAPPING'));
        }
        dispatch(adminActions.removeAdminById());
        dispatch(appointmentActions.removeAppointmentDates());
    }, []);

    useEffect(() => {
        if (resetParams) {
            const params = {
                ...resetParams,
                type: resetParams?.type,
                staff: resetParams?.staff?.id,
                startDate: resetParams?.startDate,
                startTime: resetParams.startTime?.slice(11, 16),
                endTime: resetParams.endTime?.slice(11, 16),
                creationNote: resetParams?.creationNote || '',
            };
            setInputs(params);
        }
        if (!resetParams && date) {
            setInputs({
                startTime: date?.startTime?.slice(11, 16),
                endTime: date?.endTime?.slice(11, 16),
                startDate: date?.startDate,
                staff: info?.staff,
            });
        }
    }, [resetParams, date]);

    const apptInfo = () => {
        const apptDate = {
            type: type,
            staff: inputs?.staff,
            startDate: inputs?.startDate && moment.utc(inputs?.startDate).format('YYYY-MM-DD'),
            startTime: changeDateTime(inputs?.startDate, inputs?.startTime),
            endTime: changeDateTime(inputs?.startDate, inputs?.endTime),
            editTemplate: createMultiple,
            creationNote: inputs?.creationNote || '',
        };

        if (createMultiple) {
            apptDate.endDate = moment.utc(inputs.endDate).format('YYYY-MM-DD');
            if ((modalDate && inputs?.betweenStartDate) || inputs?.betweenEndDate) {
                apptDate.updateBetween = {
                    startDate: inputs?.betweenStartDate ? moment.utc(inputs?.betweenStartDate).format('YYYY-MM-DD') : null,
                    endDate: inputs?.betweenEndDate ? moment.utc(inputs?.betweenEndDate).format('YYYY-MM-DD') : null,
                };
            }
        }
        return apptDate;
    };

    useEffect(() => {
        if (!!backError?.error === apptOverlap || !!backError?.error === staffOverlap) {
            setError(ErrorText.overlappingError('Appointments'));
            dispatch(httpRequestsOnErrorsActions.removeError('APPOINTMENT_OVERLAPPING'));
        }
    }, [backError]);

    useEffect(() => {
        return () => dispatch(adminActions.clearAllPayCodes());
    }, []);

    const handleCloseModal = () => {
        handleOpenClose && handleOpenClose();
        setInputs('');
    };

    const handleChange = (e) => {
        error === e.target.name && setError('');
        setInputs((prevState) => ({ ...prevState, [e.target.name]: e.target.value }));

        if (error === e.target.name || error === ErrorText.timeError || error === ErrorText.overlappingError('Appointments')) {
            setError('');
        }
        if (backError) {
            dispatch(httpRequestsOnErrorsActions.removeError(backError?.type));
        }
    };

    const handleCreate = () => {
        const date = apptInfo();
        const apptCreateVerification = checkBreakRequiresFields(inputs, createMultiple, type);

        if (apptCreateVerification === 'valid') {
            if (modalDate?.id) {
                dispatch(appointmentActions.editAppointment(date, modalDate?.id));
            } else {
                dispatch(appointmentActions.createAppointment(date));
            }
        } else {
            setError(apptCreateVerification);
        }
    };

    function createMultipleAppt() {
        const week = {
            startDate: new Date(inputs.startDate),
            endDate: new Date(inputs.endDate),
            mode: inputs.mode,
            repeatCountWeek: +inputs.repeatCountWeek,
            repeatCheckWeek: [...state],
        };

        const mounthObject = {
            startDate: new Date(inputs.startDate),
            endDate: new Date(inputs.endDate),
            mode: inputs.mode,
            repeatDayMonth: +inputs.repeatDayMonth,
            repeatMonth: +inputs.repeatMonth,
        };

        !inputs.repeatDayMonth ? delete mounthObject['repeatDayMonth'] : '';
        !inputs.repeatMonth ? delete mounthObject['repeatMonth'] : '';

        const newObject = {
            startDate: new Date(inputs.startDate),
            endDate: new Date(inputs.endDate),
            mode: inputs.mode,
        };

        if (inputs.repeatConsecutive === 'repeatConsecutive') {
            newObject.repeatConsecutive = true;
        } else {
            newObject.repeatCount = +inputs.repeatCount;
        }
        const obj = inputs.mode === 'WEEKLY' ? week : inputs.mode === 'MONTHLY' ? mounthObject : newObject;

        const apptVerification = checkBreakRequiresFields(inputs, createMultiple, type, state);
        if (apptVerification === 'valid') {
            open(
                <ConfMultiple
                    recurInfo={obj}
                    handleSubmit={() =>
                        dispatch(
                            appointmentActions.appointmentFromTemplate({
                                pattern: { ...obj },
                                apptData: {
                                    staffId: inputs?.staff,
                                    ...apptInfo(),
                                    template: {
                                        ...obj,
                                    },
                                },
                            })
                        )
                    }
                    loadType={APPOINTMENT_FROM_TEMPLATE}
                    occurrence={occurrence}
                    checkParams={{
                        staffId: inputs?.staff,
                        startTime: changeDateTime(obj?.startDate, inputs?.startTime),
                        endTime: changeDateTime(obj?.endDate, inputs?.endTime),
                    }}
                />
            );
        } else {
            setError(apptVerification);
        }
    }

    function editMultipleAppt() {
        if (modalDate?.id && createMultiple) {
            const apptEditVerification = checkBreakRequiresFields(inputs, false, type, state);
            if (apptEditVerification === 'valid') {
                const startTime = changeDateTime(modalDate?.startDate, inputs?.startTime);
                const endTime = changeDateTime(modalDate?.startDate, inputs?.endTime);

                const sendParams = {
                    ...apptInfo(),
                    startTime: startTime,
                    endTime: endTime,
                };
                delete sendParams.startDate;
                delete sendParams.endDate;

                open(
                    <EditMultiple
                        modalDate={{
                            // ...modalDate,
                            templateId: modalDate?.template,
                            startTime: startTime,
                            endTime: endTime,
                            staffId: inputs?.staff,
                        }}
                        handleSubmit={() => dispatch(appointmentActions.editAppointment(sendParams, modalDate?.id))}
                        loadType={editActionType}
                        occurrence={occurrence}
                        dateBetween={{
                            startDate: inputs?.betweenStartDate ? moment.utc(inputs?.betweenStartDate).format('YYYY-MM-DD') : null,
                            endDate: inputs?.betweenEndDate ? moment.utc(inputs?.betweenEndDate).format('YYYY-MM-DD') : null,
                        }}
                    />
                );
            } else {
                setError(apptEditVerification);
            }
        }
    }

    const setMultipleAppts = () => {
        setCreateMultiple(!createMultiple);
        if (modalDate) {
            const params = {
                ...inputs,
            };
            delete params.endDate;
            params.startDate = modalDate.startDate;
            setInputs(params);
        } else {
            const params = {
                ...inputs,
                ...defaultInitialInputs,
            };
            delete params.startDate;
            delete params.endDate;
            setInputs(params);
        }
    };

    const editRepeat = (e) => {
        setInputs(e);
        if (backError?.error) {
            dispatch(httpRequestsOnErrorsActions.removeError(editActionType));
        }
        if (error === 'repeat') {
            setError('');
        }
    };

    return (
        <>
            <ModalHeader modalDate={modalDate} type={type} createMultiple={createMultiple} setMultipleAppts={setMultipleAppts} />
            <div className={classes.breakWrapper}>
                <div className="full-width">
                    <p className="date-time-text">Staff</p>
                    <SelectAutocompletePagination
                        style={'full-width'}
                        defaultStatuses={[staffStatusEnums.ACTIVE]}
                        title={'Staff Member*'}
                        name={'staff'}
                        handleSelect={handleChange}
                        defaultValue={inputs?.staff}
                        renderValue={(i) => renderStaffName(i)}
                        type={'staff'}
                        error={error}
                        typeError={error === 'staff' ? `Staff member ${ErrorText.isRequired}` : ''}
                        currentSelected={
                            resetParams?.staff
                                ? resetParams?.staff
                                : info?.staffFull && reqType === reqTypeEnums.ALL
                                  ? info?.staffFull
                                  : null
                        }
                        autoSelect={reqType === reqTypeEnums.SELF && !resetParams?.id}
                    />
                </div>

                <div className="flex-align-start" style={{ gap: 24 }}>
                    {checkDateSelector(modalDate, createMultiple) && (
                        <div className="full-width">
                            <p className="date-time-text">{createMultiple ? 'Date Range' : 'Date'}</p>
                            {createMultiple ? (
                                <div className="service-inputs-box">
                                    <DateTypeSelect
                                        type={'modalInput'}
                                        name={'startDate'}
                                        outLabel={'Start Date*'}
                                        handleGetDates={handleChange}
                                        values={inputs}
                                        error={error === 'startDate' && `Start date ${ErrorText.isRequired}`}
                                        max={inputs?.endDate ? moment.utc(inputs.endDate).format('YYYY-MM-DD') : ''}
                                    />
                                    <DateTypeSelect
                                        type={'modalInput'}
                                        name={'endDate'}
                                        outLabel={'End Date*'}
                                        handleGetDates={handleChange}
                                        values={inputs}
                                        error={error === 'endDate' && `End date ${ErrorText.isRequired}`}
                                        min={inputs?.startDate ? moment.utc(inputs.startDate).format('YYYY-MM-DD') : ''}
                                    />
                                </div>
                            ) : (
                                <DateTypeSelect
                                    type={'modalInput'}
                                    name={'startDate'}
                                    outLabel={'Start Date*'}
                                    handleGetDates={handleChange}
                                    values={inputs}
                                    error={error === 'startDate' && `Start date ${ErrorText.isRequired}`}
                                />
                            )}
                        </div>
                    )}

                    <div className="full-width">
                        <p className="date-time-text">{'Time'}</p>
                        <div className={classes.timeInputs} style={{ gap: '16px' }}>
                            <TimeInput
                                label={'Start Time*'}
                                name={'startTime'}
                                onChange={handleChange}
                                typeError={
                                    error === 'startTime'
                                        ? `Start time ${ErrorText.isRequired}`
                                        : backError?.error === apptOverlap
                                          ? ErrorText.overlappingError('Appointments')
                                          : backError?.error === staffOverlap
                                            ? ErrorText.overlappingError('Appointments')
                                            : ''
                                }
                                defaultValue={inputs?.startTime ? renderTimes(inputs.startTime) || inputs.startTime : null}
                            />
                            <TimeInput
                                label={'End Time*'}
                                name={'endTime'}
                                onChange={handleChange}
                                typeError={
                                    error === 'endTime'
                                        ? `End time ${ErrorText.isRequired}`
                                        : error === ErrorText.timeError
                                          ? ErrorText.timeError
                                          : ''
                                }
                                defaultValue={inputs?.endTime ? renderTimes(inputs.endTime) || inputs.endTime : null}
                            />
                        </div>
                    </div>
                </div>

                <div className="flex-align-start" style={{ gap: 24 }}>
                    <div className="full-width">
                        <p className="date-time-text">{'Note'}</p>
                        <Description
                            name={'creationNote'}
                            handleChange={handleChange}
                            value={inputs?.creationNote}
                            label={'Note'}
                            maxLength={250}
                            allowCharacter
                        />
                    </div>
                    <div className="full-width" />
                </div>

                {modalDate?.id && createMultiple && (
                    <div className="template-date-range">
                        <p className="date-time-text">Date Range</p>
                        <div className="service-inputs-box">
                            <DateTypeSelect
                                type={'modalInput'}
                                name={'betweenStartDate'}
                                outLabel={'Start Date'}
                                handleGetDates={handleChange}
                                values={inputs}
                                error={error === 'betweenStartDate' && `Start date ${ErrorText.isRequired}`}
                                noError={true}
                                min={apptBetweenDates?.startDate}
                                max={apptBetweenDates?.endDate}
                            />
                            <DateTypeSelect
                                type={'modalInput'}
                                name={'betweenEndDate'}
                                outLabel={'End Date'}
                                handleGetDates={handleChange}
                                values={inputs}
                                error={error === 'betweenEndDate' && `End date ${ErrorText.isRequired}`}
                                noError={true}
                                min={apptBetweenDates?.startDate}
                                max={apptBetweenDates?.endDate}
                            />
                        </div>

                        <div className="changes-will-apply">
                            <img src={Images.toolInfoIcon} alt={'icon'} />
                            <p>Changes will apply to all appointments between these dates.</p>
                        </div>
                    </div>
                )}
                {createMultiple && (
                    <CreateMultiple
                        handleClose={handleOpenClose}
                        modalDate={modalDate}
                        state={state}
                        setState={setState}
                        inputs={{ ...inputs }}
                        setInputs={editRepeat}
                        setOccurrence={setOccurrence}
                        error={error}
                    />
                )}

                <div className={classes.actionBtnBox}>
                    {modalDate?.id && createMultiple ? (
                        <TemplateApptActions
                            apptDate={modalDate}
                            handleClose={handleOpenClose}
                            handleSave={editMultipleAppt}
                            loader={!!loader.length}
                            // disabled={errorList?.length !== 0}
                            apptBetweenDates={apptBetweenDates}
                        />
                    ) : (
                        <CreateChancel
                            buttonWidth={'100%'}
                            cancelRight={'24px'}
                            // buttonWidth="401px"
                            // cancelRight={'0'}
                            loader={!!loader.length}
                            create={
                                modalDate?.id && createMultiple
                                    ? 'Update'
                                    : modalDate
                                      ? 'Save'
                                      : createMultiple
                                        ? `Add ${occurrence ? occurrence : 0} Block Times`
                                        : 'Block Time'
                            }
                            chancel={'Cancel'}
                            onCreate={createMultiple ? createMultipleAppt : handleCreate}
                            onClose={handleCloseModal}
                            // disabled={errorList?.length !== 0}
                        />
                    )}
                </div>
            </div>
        </>
    );
};
